'use client';
// RouteErrorBoundary - Error boundary specifically for routes/pages
import { Component, ErrorInfo, ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { Box, Button, Container, Typography } from '@mui/material';

interface RouteErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  routeName?: string;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class RouteErrorBoundaryClass extends Component<
  RouteErrorBoundaryProps & { navigate: (path: string) => void },
  RouteErrorBoundaryState
> {
  constructor(props: RouteErrorBoundaryProps & { navigate: (path: string) => void }) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error(
      `RouteErrorBoundary caught an error${this.props.routeName ? ` in ${this.props.routeName}` : ''}:`,
      error,
      errorInfo
    );
    // You can log to an error reporting service here
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  handleGoHome = (): void => {
    this.props.navigate('/');
    this.handleReset();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
              Something went wrong
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
              {this.props.routeName
                ? `An error occurred on the ${this.props.routeName} page.`
                : 'An unexpected error occurred.'}
            </Typography>
            {this.state.error && (
              <Typography
                variant="body2"
                sx={{ mb: 4, color: 'error.main', fontFamily: 'monospace' }}
              >
                {this.state.error.message}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={this.handleReset}
                sx={{
                  bgcolor: '#2563eb',
                  '&:hover': {
                    bgcolor: '#1d4ed8',
                  },
                }}
              >
                Try Again
              </Button>
              <Button
                variant="outlined"
                onClick={this.handleGoHome}
                sx={{
                  borderColor: '#2563eb',
                  color: '#2563eb',
                  '&:hover': {
                    borderColor: '#1d4ed8',
                    bgcolor: 'rgba(37, 99, 235, 0.04)',
                  },
                }}
              >
                Go Home
              </Button>
            </Box>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}

// Wrapper component to use hooks
export const RouteErrorBoundary = ({ children, fallback, routeName }: RouteErrorBoundaryProps) => {
  const router = useRouter();

  return (
    <RouteErrorBoundaryClass navigate={router.push} fallback={fallback} routeName={routeName}>
      {children}
    </RouteErrorBoundaryClass>
  );
};

export default RouteErrorBoundary;
